// IN-list head scan — value-slot plan T17. Pure text scan (G11-rule: no
// tree-sitter); split out of `editorContextSlot.value.ts` mirroring the
// setScan/betweenScan modules.
//
// Determines whether the cursor's ENCLOSING paren group is the argument list
// of a scalar `[NOT] IN ( … )` — i.e. the group's opener is immediately
// preceded by the `IN` keyword — with NO `SELECT` keyword at that group's own
// depth between the `(` and the scan end (a subquery interior is the clause
// machine's business, G12). Non-IN groups (function calls, bare tuples,
// scalar-subquery parens) and subquery interiors return undefined.
//
// The LHS handed back is the operand END offset just before `[NOT] IN`; the
// recognizer runs `scanLhsOperand` there and applies the F23 multi-column
// refusal (a bare `(a, b)` tuple head is NOT a single-column IN).

import {
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
} from './scanPrimitives';

export type InListHeadMatch = {
  /** True for `NOT IN`. */
  readonly negated: boolean;
  /** Offset of `IN`'s first char (of `NOT` when negated) — the LHS operand
   *  ends before this. Mirrors `ComparisonOperatorMatch.opStart`. */
  readonly opStart: number;
};

type GroupFrame = {
  readonly inHead: InListHeadMatch | undefined;
  sawSelect: boolean;
};

/** Scans `[from, until)` — `until` is the cursor offset (or, for an
 *  in-progress string element, its opening quote) — and returns the IN-head
 *  match when the innermost open group at `until` is a scalar IN list. */
export function scanInListHead(
  source: string,
  from: number,
  until: number,
  engine: string,
): InListHeadMatch | undefined {
  const stack: GroupFrame[] = [];
  let i = from;
  while (i < until) {
    const c = source.charCodeAt(i);
    if (c === 0x2d /* - */ && source.charCodeAt(i + 1) === 0x2d) {
      i = skipToEol(source, i + 2);
      continue;
    }
    if (c === 0x2f /* / */ && source.charCodeAt(i + 1) === 0x2a) {
      i = skipBlockComment(source, i + 2);
      continue;
    }
    if (c === 0x27 /* ' */) {
      i = skipSingleQuoted(source, i + 1);
      continue;
    }
    if (c === 0x22 /* " */) {
      i = skipDoubleQuoted(source, i + 1);
      continue;
    }
    if ((engine === 'mssql' || engine === 'sqlite') && c === 0x5b /* [ */) {
      i = skipBracketedId(source, i + 1);
      continue;
    }
    if (engine === 'mysql' && c === 0x60 /* ` */) {
      i = skipBacktickId(source, i + 1);
      continue;
    }
    if (c === 0x28 /* ( */) {
      stack.push({ inHead: matchInBefore(source, i, from), sawSelect: false });
      i += 1;
      continue;
    }
    if (c === 0x29 /* ) */) {
      stack.pop();
      i += 1;
      continue;
    }
    if (/[A-Za-z_]/.test(source[i]!)) {
      let j = i + 1;
      while (j < until && /[A-Za-z0-9_$]/.test(source[j]!)) j += 1;
      if (
        stack.length > 0 &&
        j - i === 6 &&
        source.slice(i, j).toUpperCase() === 'SELECT'
      ) {
        stack[stack.length - 1]!.sawSelect = true;
      }
      i = j;
      continue;
    }
    i += 1;
  }
  const top = stack[stack.length - 1];
  if (!top || top.inHead === undefined || top.sawSelect) return undefined;
  return top.inHead;
}

/** `[NOT] IN` ending (ws-tolerant) just before the `(` at `openIdx`. */
function matchInBefore(
  source: string,
  openIdx: number,
  lowBound: number,
): InListHeadMatch | undefined {
  let k = openIdx;
  while (k > lowBound && /\s/.test(source[k - 1]!)) k -= 1;
  let w = k;
  while (w > lowBound && /[A-Za-z]/.test(source[w - 1]!)) w -= 1;
  if (source.slice(w, k).toUpperCase() !== 'IN') return undefined;
  // Reject an identifier tail that merely ENDS in "in" (`margin(`): the char
  // before the run must not extend the word.
  if (w > lowBound && /[0-9_$]/.test(source[w - 1]!)) return undefined;
  let k2 = w;
  while (k2 > lowBound && /\s/.test(source[k2 - 1]!)) k2 -= 1;
  let nw = k2;
  while (nw > lowBound && /[A-Za-z]/.test(source[nw - 1]!)) nw -= 1;
  if (source.slice(nw, k2).toUpperCase() === 'NOT') {
    return { negated: true, opStart: nw };
  }
  return { negated: false, opStart: w };
}
